import * as React from 'react';

import { useKeyListener, useTranslation } from '@components';
import { storageGet, storageSet } from '@utils/storage';

import InputEventDispatcher from './InputEventDispatcher';

type InputSearchFilterCallback = (_: string | null | undefined) => void;

type InputSearchFilterProps = {
    filterKey: string;
    onChange?: InputSearchFilterCallback;
    title?: string;
    focus?: boolean;
}

export function InputSearchFilter(props: InputSearchFilterProps) {
    const { t } = useTranslation();
    const searchInput = React.useRef<any>();

    const [filter, setFilter] = React.useState<string | null | undefined>(() => {
        const storedValue = storageGet<string | null | undefined>(props.filterKey, '');
        return storedValue === '' ? null : storedValue;
    });

    const dispatcher = React.useRef(
        new InputEventDispatcher<string | null | undefined>(
            (value: string | null | undefined) => {
                const processedValue = value === '' ? null : value;
                setFilter(processedValue);
                props.onChange?.(processedValue);
            },
            setFilter,
        ));

    React.useEffect(() => {
        storageSet(props.filterKey, filter == null ? '' : filter);
    }, [filter]);

    if (props.focus) {
        useKeyListener({
            key: 'f',
            keyCtrl: true,
            handle: () => {
                searchInput.current.focus();
            }
        });
    }

    return <input
        ref={searchInput}
        className='e flat'
        // onChange={e => setFilter(e.target.value)}
        onChange={e => dispatcher.current.append(e)}
        type='search'
        value={filter ?? ''}
        placeholder={t(props.title ?? 'Search')} />
}

export function useInputSearch(props: InputSearchFilterProps) {
    const [filter, setFilter] = React.useState<string | null | undefined>(() => {
        const storedValue = storageGet<string | null | undefined>(props.filterKey, '');
        return storedValue === '' ? null : storedValue;
    });

    const onInput = (search: string | null | undefined) => {
        const processedSearch = search === '' ? null : search;
        setFilter(processedSearch);
        props.onChange?.(processedSearch);
    }

    React.useEffect(() => {
        storageSet(props.filterKey, filter == null ? '' : filter);
    }, [filter]);

    const render = () => {
        return <InputSearchFilter {...props} onChange={onInput} />;
    }

    return {
        render: render,
        value: filter,
    }
}
